import { useState, useEffect } from 'react'
import { styled, SxProps } from '@mui/material/styles'
import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import FormHelperText from '@mui/material/FormHelperText'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import { useTranslation } from 'react-i18next'
import find from 'lodash/find'
import cloneDeep from 'lodash/cloneDeep'

import { useAbstractRolesTypeOptions, getOption, Options, useFetchUsers } from '../../../utils'
import { useCurrentUserQuery } from '../../../services/user'
import { AuthorsSchema } from '../Abstracts.schema'
import { User, AbstractRolesType } from '../../../types'

const Info = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 2,
  textAlign: 'center',
}))

interface AuthorsAutocompleteProps {
  prependName: string
  defaultValue?: AuthorsSchema[] | null
  onChange?: (value: AuthorsSchema[]) => void
  label: React.ReactNode
  readOnly?: boolean
  variant?: TextFieldProps['variant']
  sx?: SxProps
  error?: boolean
  helperText?: string
}

interface AuthorOption {
  role: AbstractRolesType | null
  users: (Options & User)[]
  multiple?: boolean
  required?: boolean
  fixedOption?: boolean
  assignMe?: boolean
}

const defaultAuthorOptions = () =>
  cloneDeep([
    {
      role: AbstractRolesType.Speaker,
      users: [],
      fixedOption: true,
      required: true,
      assignMe: true,
    },
    {
      role: AbstractRolesType.Author,
      users: [],
      fixedOption: true,
      required: true,
      assignMe: true,
    },
    {
      role: AbstractRolesType.CoAuthor,
      users: [],
      fixedOption: true,
      multiple: true,
      assignMe: true,
    },
  ]) as AuthorOption[]

const getDefaultValue = (value?: AuthorsSchema[] | null): AuthorOption[] => {
  if (!value?.length) return defaultAuthorOptions()

  const res = value.reduce((acc, elem) => {
    elem.roles.forEach(role => {
      const index = acc.findIndex(e => e.role === role)
      const user = {
        ...elem.user,
        value: elem.user.id,
        label: `${elem.user.first_name} ${elem.user.last_name}`,
      }
      if (index !== -1) {
        acc[index].users.push(user)
      } else {
        acc.push({ role, users: [user] })
      }
    })
    return acc
  }, defaultAuthorOptions())

  return res
}

export default function AuthorsAutocomplete({
  prependName,
  label,
  defaultValue,
  onChange,
  readOnly,
  variant,
  sx,
  error,
  helperText,
}: AuthorsAutocompleteProps) {
  const { t } = useTranslation()
  const roleOptions = useAbstractRolesTypeOptions(t)
  const { data: currentUser } = useCurrentUserQuery()

  const [authors, setAuthors] = useState<AuthorOption[]>(getDefaultValue(defaultValue))

  const { fetch, loading, users } = useFetchUsers()

  useEffect(() => {
    if (onChange) {
      const res = authors.reduce((acc, elem) => {
        elem.users.forEach(user => {
          const index = acc.findIndex(e => e.user.id === user.id)
          if (index !== -1) {
            acc[index].roles.push(elem.role!)
          } else {
            acc.push({ user, roles: [elem.role!] })
          }
        })
        return acc
      }, [] as AuthorsSchema[])
      onChange(res ?? [])
    }
  }, [authors, onChange])

  return (
    <Stack padding="0 1rem" spacing={1} sx={sx}>
      <Stack direction="row" alignItems="center" justifyContent="start" spacing={1}>
        {label}
      </Stack>
      <Divider sx={{ margin: 0 }} />

      {authors.length ? (
        authors.map((author, index) => (
          <Stack direction="row" alignItems="center" spacing={1} key={index}>
            <Stack direction="row" flex="1 0">
              <Autocomplete
                disablePortal
                id="role-autocomplete"
                value={getOption(roleOptions, null, author.role) ?? undefined}
                options={roleOptions.filter(
                  option => !find(authors, el => el?.role === option.value)
                )}
                fullWidth
                readOnly={author.fixedOption ?? readOnly}
                popupIcon={(author.fixedOption ?? readOnly) ? null : undefined}
                disableClearable
                onChange={(_, opt) => {
                  const newRoles = [...authors]
                  newRoles[index] = { ...authors[index], role: opt.value }
                  setAuthors(newRoles)
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('Role')}
                    name={`${prependName}.role`}
                    defaultValue={author.role}
                    variant={variant}
                    required={!!author.users?.length || !!author.role}
                    slotProps={{
                      input: { ...params.InputProps, readOnly: author.fixedOption ?? readOnly },
                    }}
                    error={error}
                    sx={{
                      minHeight: '100%',
                      fieldset: {
                        borderRightWidth: 1,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                      '.MuiInputBase-root': {
                        minHeight: '100%',
                        flex: '1 0 auto',
                      },
                      ':hover': {
                        fieldset: {
                          borderRightWidth: 2,
                        },
                      },
                    }}
                  />
                )}
                sx={{ maxWidth: '200px', minWidth: '200px', minHeight: '100%' }}
              />
              <Autocomplete
                disablePortal
                id="user-autocomplete"
                options={users}
                multiple
                getOptionDisabled={() =>
                  author.multiple ? false : author.users.length >= 1 ? true : false
                }
                fullWidth
                readOnly={readOnly}
                popupIcon={readOnly ? null : undefined}
                filterOptions={x => x}
                value={author.users ?? undefined}
                onChange={(_, opts) => {
                  const newRoles = [...authors]
                  newRoles[index] = { ...authors[index], users: opts }
                  setAuthors(newRoles)
                }}
                onInputChange={(_, newInputValue) => {
                  fetch(newInputValue)
                }}
                loading={loading}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('Utilisateurs')}
                    name={`${prependName}.role.${author.role}.user`}
                    variant={variant}
                    required={author.required}
                    error={error}
                    sx={{
                      fieldset: {
                        borderLeftWidth: 1,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                      ':hover': {
                        fieldset: {
                          borderLeftWidth: 2,
                        },
                      },
                    }}
                  />
                )}
                sx={{ flex: '1 1 auto' }}
              />
            </Stack>
            {!!author.assignMe && currentUser && (
              <Tooltip title={t("M'assigner")}>
                <IconButton
                  color="primary"
                  onClick={() => {
                    const me = {
                      ...currentUser,
                      value: currentUser.id,
                      label: `${currentUser.first_name} ${currentUser.last_name}`,
                    }
                    const newRoles = [...authors]
                    newRoles[index] = {
                      ...authors[index],
                      users: author.multiple
                        ? [...author.users.filter(el => el.id !== me.id), me]
                        : [me],
                    }
                    setAuthors(newRoles)
                  }}
                  sx={{ height: 'auto' }}
                >
                  <AssignmentIndIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))
      ) : (
        <Info>
          {readOnly ? t('Aucun contributeur') : t('Ajouter des rôles et des utilisateurs')}
        </Info>
      )}
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </Stack>
  )
}
